import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Languages } from "lucide-react";
import { toast } from "sonner";

const BUSINESS_TYPES = ["ristorante", "pizzeria", "bar", "hotel", "mensa", "catering"];

export default function LoginPage() {
  const { setData } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("tab") === "register" ? "register" : "login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", org_name: "", business_type: "ristorante", seats: 50,
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "register"
        ? { ...form, seats: Number(form.seats) || 50 }
        : { email: form.email, password: form.password };
      await api.post(`/auth/${mode}`, payload);
      const me = await api.get("/auth/me");
      setData(me.data);
      const locs = me.data.locations || [];
      if (locs.length) localStorage.setItem("sk_loc", locs[0].location_id);
      navigate(me.data.user.org_id ? "/dashboard" : "/onboarding", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/12193823/pexels-photo-12193823.jpeg"
          alt="Professional kitchen"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="T2WORK KITCHEN" className="h-16 w-16 rounded-2xl object-contain bg-white p-1.5 shadow-xl" />
            <span className="font-display font-extrabold text-2xl">{t("appName")}</span>
          </div>
          <div className="space-y-4 max-w-md">
            <h1 className="font-display text-4xl font-extrabold leading-tight">{t("loginSubtitle")}</h1>
            <p className="text-slate-300 text-sm leading-relaxed">
              HACCP · Magazzino · Scadenze · Sprechi · Temperature — {lang === "it" ? "in pochi tocchi." : "in a few taps."}
            </p>
          </div>
          <p className="text-xs text-slate-400">© 2026 T2WORK KITCHEN — t2work.net</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6 animate-fade-up">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 lg:hidden">
              <img src="/logo.png" alt="T2WORK KITCHEN" className="h-10 w-10 rounded-xl object-contain bg-white border p-0.5" />
              <span className="font-display font-extrabold text-lg">{t("appName")}</span>
            </div>
            <button data-testid="login-language-toggle" onClick={toggle}
              className="tactile-btn h-9 px-3 rounded-lg border text-xs font-bold uppercase flex items-center gap-1.5 ml-auto">
              <Languages className="h-4 w-4" />{lang}
            </button>
          </div>

          <div className="bg-card border rounded-2xl shadow-sm p-6 sm:p-8 space-y-6">
            <div className="grid grid-cols-2 gap-1 p-1 bg-secondary rounded-xl" data-testid="auth-mode-tabs">
              <button data-testid="tab-login" onClick={() => setMode("login")}
                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                {t("login")}
              </button>
              <button data-testid="tab-register" onClick={() => setMode("register")}
                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                {t("register")}
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label>{t("name")}</Label>
                  <Input data-testid="register-name" required value={form.name} onChange={set("name")} className="h-12" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>{t("email")}</Label>
                <Input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} className="h-12" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("password")}</Label>
                <Input data-testid="auth-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="h-12" />
              </div>
              {mode === "register" && (
                <>
                  <div className="space-y-1.5">
                    <Label>{t("businessName")}</Label>
                    <Input data-testid="register-business-name" required value={form.org_name} onChange={set("org_name")} className="h-12" placeholder="Ristorante Da Mario" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>{t("businessType")}</Label>
                      <select data-testid="register-business-type" value={form.business_type} onChange={set("business_type")}
                        className="h-12 w-full rounded-md border bg-background px-3 text-sm">
                        {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b[0].toUpperCase() + b.slice(1)}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("seats")}</Label>
                      <Input data-testid="register-seats" type="number" min={1} value={form.seats} onChange={set("seats")} className="h-12" />
                    </div>
                  </div>
                </>
              )}
              <Button data-testid="auth-submit-button" type="submit" disabled={busy}
                className="w-full h-12 text-base font-bold tactile-btn">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? t("login") : t("register")}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground font-semibold">oppure / or</span>
              </div>
            </div>

            <Button data-testid="google-login-button" type="button" variant="outline" onClick={googleLogin}
              className="w-full h-12 text-sm font-semibold tactile-btn">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
                <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"/>
                <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C12 6.87 8.87 4.76 12 4.76z"/>
              </svg>
              {t("continueGoogle")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
